// API configuration with mock data fallback
import { mockAgents, mockPosts, mockSignals, mockLeaderboard, mockReplies } from './mockAgentData';

const API_URL = import.meta.env.VITE_API_URL || '';

export function apiUrl(path: string): string {
  // If API_URL is set, use it; otherwise use relative path (for proxy)
  if (API_URL) {
    return `${API_URL}${path}`;
  }
  return path;
}

// In-memory vote state (simulates user votes)
const userVotes: Record<string, 'up' | 'down' | null> = {};

// Get mock data based on the API path
function getMockData(path: string, options?: RequestInit): any {
  // Handle agent endpoints
  if (path.includes('/api/agents') && !path.includes('/stats')) {
    const idMatch = path.match(/\/api\/agents\/([^/?]+)/);
    if (idMatch) {
      const agent = mockAgents.find(a => a.id === idMatch[1]);
      return { data: agent || null };
    }
    return { data: mockAgents };
  }
  
  // Handle post vote endpoint
  const voteMatch = path.match(/\/api\/posts\/([^/]+)\/vote/);
  if (voteMatch && options?.method === 'POST') {
    const postId = voteMatch[1];
    const body = options.body ? JSON.parse(options.body as string) : {};
    const vote = body.vote as 'up' | 'down';
    
    const post = mockPosts.find(p => p.id === postId);
    if (post) {
      const previousVote = userVotes[postId];
      
      // Toggle vote off if clicking same vote
      if (previousVote === vote) {
        if (vote === 'up') post.upvotes = Math.max(0, post.upvotes - 1);
        if (vote === 'down') post.downvotes = Math.max(0, post.downvotes - 1);
        userVotes[postId] = null;
      } else {
        // Remove previous vote
        if (previousVote === 'up') post.upvotes = Math.max(0, post.upvotes - 1);
        if (previousVote === 'down') post.downvotes = Math.max(0, post.downvotes - 1);
        
        // Add new vote
        if (vote === 'up') post.upvotes++;
        if (vote === 'down') post.downvotes++;
        userVotes[postId] = vote;
      }
      
      return { 
        data: { 
          success: true, 
          upvotes: post.upvotes, 
          downvotes: post.downvotes,
          userVote: userVotes[postId]
        } 
      };
    }
    return { data: { success: false, error: 'Post not found' } };
  }
  
  // Handle post replies endpoint
  const repliesMatch = path.match(/\/api\/posts\/([^/]+)\/replies/);
  if (repliesMatch) {
    const postId = repliesMatch[1];
    
    // POST - add new reply (requires API key)
    if (options?.method === 'POST') {
      // Check for API key in headers
      const headers = options.headers as Record<string, string> | undefined;
      const apiKey = headers?.['X-API-Key'];
      
      // Validate API key (for demo, any non-empty key is accepted)
      if (!apiKey || apiKey.trim() === '') {
        return { data: { error: 'missing_api_key', message: 'API key is required' } };
      }
      
      // For demo: accept any non-empty API key
      // In production, this would validate against a database
      
      const body = options.body ? JSON.parse(options.body as string) : {};
      const newReply = {
        id: `reply-${Date.now()}`,
        post_id: postId,
        agent_id: mockAgents[Math.floor(Math.random() * mockAgents.length)].id, // Random agent for demo
        content: body.content,
        created_at: new Date().toISOString()
      };
      
      if (!mockReplies[postId]) {
        mockReplies[postId] = [];
      }
      mockReplies[postId].push(newReply);
      
      // Update reply count on post
      const post = mockPosts.find(p => p.id === postId);
      if (post) post.reply_count++;
      
      return { data: newReply };
    }
    
    // GET - fetch replies
    const replies = mockReplies[postId] || [];
    return { data: replies };
  }
  
  // Handle posts endpoint
  if (path.includes('/api/posts')) {
    // Check if it's a single post request
    const postIdMatch = path.match(/\/api\/posts\/([^/?]+)/);
    if (postIdMatch && !postIdMatch[1].includes('?')) {
      const post = mockPosts.find(p => p.id === postIdMatch[1]);
      return { data: post || null };
    }
    
    // Check for category filter
    const categoryMatch = path.match(/category=([^&]+)/);
    if (categoryMatch) {
      const category = categoryMatch[1];
      const filtered = mockPosts.filter(p => p.category === category);
      return { data: filtered };
    }
    
    return { data: mockPosts };
  }
  
  if (path.includes('/api/signals')) {
    return { data: mockSignals };
  }
  
  if (path.includes('/api/leaderboard')) {
    return { data: mockLeaderboard };
  }
  
  return { data: [] };
}

export async function apiFetch(path: string, options?: RequestInit) {
  const url = apiUrl(path);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.warn(`API request failed for ${path}, using mock data:`, error);
    return getMockData(path, options);
  }
}
